<script>
    import { onMount } from 'svelte';
    import { slide } from 'svelte/transition';

    import { config } from '../config.js';
    import { referenceDataKeyworkerList } from "../reference-data";
    import { getApiDateFormat, getTimeStringFromDate, getIsoDateStringAsDate, 
        getUkCalendarDateStringFromDate } from '../date-time.js';

    import Toggle from '../component/Toggle.svelte';
    import Dropdown from '../component/Dropdown.svelte';

    export let contactDate;
    export let contactTime;
    export let contactNotes;
    export let seenBy;
    export let followUp;
    export let followUpDate;
    export let followUpAction;
    export let isFormValid;
    export let showValidation;

    // Staff member who recorded follow-up on previous contact - Used to check if different from current staff member 
    export let previousFollowUpStaff = null;
    
    $: seenByName = seenBy != null ? ($referenceDataKeyworkerList.find(k => k.staffUrn === seenBy)?.name) : null;

    onMount(() => {
        let today = new Date();
        
        // Set default contact date and time to now
        contactDate = getApiDateFormat(today);
        contactTime = getTimeStringFromDate(today);

        // Set follow-up date to the configured period, or default to 7 days if configuration is not specified
        today.setDate(today.getDate() + ($config.contactReviewDateDefaultPeriod ? $config.contactReviewDateDefaultPeriod : 7));
        followUpDate = getApiDateFormat(today);
    });

    // Contact Date Validation
    let isContactDateValid;
    let isContactTimeValid;
    let contactDateValidationMessage = null;
    $: {
         // Validation: Contact Date and Time cannot be empty
        if (!contactDate || !contactTime) {
            isContactDateValid = contactDate ? true : false;
            isContactTimeValid = contactTime ? true : false;
            contactDateValidationMessage = "Date and time are required";
        } else {
            let selectedDate = getIsoDateStringAsDate(contactDate);
            selectedDate.setHours(contactTime.substring(0,2),contactTime.substring(3,5),0,0);
            let today = new Date();

            // Validation: Contact Date cannot be in the future
            if (selectedDate > today) {
                isContactDateValid = false;
                isContactTimeValid = false;
                contactDateValidationMessage = "Must not be a date in the future";
            } else {
                isContactDateValid = true;
                isContactTimeValid = true;
                contactDateValidationMessage = null;
            }
        }
    }

    // Follow-up Date Validation
    let isFollowUpDateValid;
    let followUpDateValidationMessage = null;
    $: {
        // Validation: Follow-up Date cannot be empty
        if (!followUpDate) {
            isFollowUpDateValid = false;
            followUpDateValidationMessage = "Date is required";
        } else {
            let selectedDate = getIsoDateStringAsDate(followUpDate);
            let today = new Date();
            today.setHours(23,59,59,999);

            // Validation: Follow-up Date must be in the future
            if (selectedDate <= today) {
                isFollowUpDateValid = false;
                followUpDateValidationMessage = "Must be a date in the future";
            } else {
                // Validation: Follow-up Date cannot be later than a month from the contact date
                if (contactDate) {
                    let selectedContactDate = getIsoDateStringAsDate(contactDate);
                    selectedContactDate.setMonth(selectedContactDate.getMonth() + 1);
                    
                    if (selectedDate > selectedContactDate) {
                        isFollowUpDateValid = false;
                        followUpDateValidationMessage = "Cannot be later than " + getUkCalendarDateStringFromDate(selectedContactDate);
                    } else {
                        isFollowUpDateValid = true;
                        followUpDateValidationMessage = null;
                    }
                } else {
                    isFollowUpDateValid = true;
                    followUpDateValidationMessage = null;
                }
            }
        }
    }

    $: isSeenByValid = seenBy !== null && seenBy !== undefined;
    $: isNotesValid = contactNotes && contactNotes.length >= 4;

    // Check if all fields are valid
    $: { isFormValid = isContactDateValid && isContactTimeValid && isSeenByValid && isNotesValid
            && (!followUp || (followUp && isFollowUpDateValid)); 
    }

    // Remove the 'Not Recorded' staff member from the list
    $: staffList = $referenceDataKeyworkerList.filter(keyworker => keyworker.staffUrn !== -1);
</script>

<style>
    .col-contact-label {
        flex: 0 0 18%;
        max-width: 18%;
    }
</style>

<div class="form-row align-items-center" class:invalid={showValidation && contactDateValidationMessage}>
    <div class="col-contact-label">
        <label for="contact-date" class="col-form-label">Contact on</label>
    </div>
    <div class="col-auto">
        <input id="contact-date" type="date" class="form-control" 
            bind:value={contactDate} class:invalid={!isContactDateValid && showValidation} />
    </div>
    <div class="col-auto">
        <label class="sr-only" for="contact-time">Contact Time</label>
        <input id="contact-time" type="time" class="form-control" 
            bind:value={contactTime} class:invalid={!isContactTimeValid && showValidation} />
    </div>
</div>

{#if contactDateValidationMessage && showValidation}
    <div class="form-row modal-form-error">
        <div class="col-contact-label" />
        <div class="col-auto">
            <i class="bi bi-exclamation-circle-fill" />
            {contactDateValidationMessage}
        </div>
    </div>
{/if}

<div class="form-row align-items-center" class:invalid={(previousFollowUpStaff && seenByName && (previousFollowUpStaff != seenByName)) || (!isSeenByValid && showValidation)}>
    <div class="col-contact-label">
        <label for="seen-by" class="col-form-label">Seen By</label>
    </div>
    <div class="col-auto">
        <Dropdown bind:value={seenBy} id={"seen-by"} srLabel={false} options={staffList} 
            valueProperty={"staffUrn"} labelProperty={"name"} classes={!isSeenByValid && showValidation ? 'invalid' : ''} />
    </div>
</div>

{#if !isSeenByValid && showValidation}
    <div class="form-row modal-form-error">
        <div class="col-contact-label" />
        <div class="col-auto">
            <i class="bi bi-exclamation-circle-fill" />
            Seen by is required
        </div>
    </div>
{/if}

{#if previousFollowUpStaff && seenByName && (previousFollowUpStaff != seenByName)}
    <div class="form-row modal-form-warning">
        <div class="col-contact-label" />
        <div class="col-auto">
            <i class="bi bi-exclamation-triangle-fill" />
            The previous follow-up action was recorded by {previousFollowUpStaff}. Saving this contact will mark their follow-up as complete.
        </div>
    </div>
{/if}

<div class="form-row align-items-center">
    <label for="notes" class="d-none">Contact Notes</label>
    <div class="col-12">
        <textarea id="notes" class="form-control" placeholder="Please enter contact details."
            rows="4" bind:value={contactNotes} class:invalid={!isNotesValid && showValidation} />
    </div>
</div>

<div class="form-row align-items-center" class:invalid={showValidation && followUpDateValidationMessage}>
    <div class="col-contact-label">
        <label for="follow-up-date" class="col-form-label">Follow up?</label>
    </div>
    <div class="col-auto">
        <Toggle bind:checked={followUp} offWarning={true} ariaLabel={"Follow up required?"} />
    </div>
    {#if followUp}
        <div class="col-auto">
            <input id="follow-up-date" type="date" class="form-control" 
                bind:value={followUpDate} class:invalid={!isFollowUpDateValid && showValidation} />
        </div>
    {/if}
</div>

{#if followUp && followUpDateValidationMessage && showValidation}
    <div class="form-row modal-form-error">
        <div class="col-contact-label" />
        <div class="col-auto">
            <i class="bi bi-exclamation-circle-fill" />
            {followUpDateValidationMessage}
        </div>
    </div>
{/if}

{#if followUp}
    <div transition:slide|local={{ duration : 400 }} class="form-row align-items-center">
        <label for="action" class="d-none">Follow-up Action</label>
        <div class="col-12">
            <textarea id="action" class="form-control" placeholder="Please enter follow-up action."
                rows="4" bind:value={followUpAction} />
        </div>
    </div>
{/if}